import axios from 'axios'
import moment from 'moment-timezone'
import { useCallback, useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { Tooltip } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'
import { KTSVG } from '../../../_metronic/helpers'
import DateComp from '../../../components/dateComponent/DateComp'
import DateTimeComp from '../../../components/dateComponent/DateTimeComp'
import { constraintConfig } from '../../../constraintConfig'
import { selectAuth } from '../../../features/authSlice'
import { useAppSelector } from '../../../hooks'
import { API_END_POINTS } from '../../../services/apiEndpoints'
import { useLazyGetOrgUserLoggedInPresenceQuery } from '../../../services/authApi'
import { subscribeUserPresence, UserPresencePayload } from '../../../services/ssExpensesHub'
import labelKey from "../../localization/label.json"
import { buildPresenceTooltipHtml, LoginSourceIcon } from './userLoginSource'

type Props = {
    show: boolean
    handleClose: () => void
    data: any
}

const canWatchUserPresence = (roleId?: number | null) =>
    roleId === constraintConfig.roleID.role1
    || roleId === constraintConfig.roleID.role2
    || roleId === constraintConfig.roleID.roleIDPortalSupport
    || roleId === constraintConfig.roleID.roleIDAdmin

const UserDetailModal = ({ show, handleClose, data }: Props) => {
    const { token, baseURL, roleID, timeZone } = useAppSelector(selectAuth);
    const [singleUserData, setSingleUserData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [presence, setPresence] = useState<UserPresencePayload | null>(null);
    const [fetchPresenceSnapshot] = useLazyGetOrgUserLoggedInPresenceQuery();
    const showPresence = canWatchUserPresence(roleID);
    const viewerTimeZone = timeZone || 'UTC';
    const userId = Number(data);

    const formatUtcInViewerTz = useCallback((utc?: string | Date | null) => {
        if (!utc) return ''
        const parsed = moment.utc(utc)
        if (!parsed.isValid()) return ''
        return parsed.tz(viewerTimeZone).format('MM-DD-YYYY hh:mm A')
    }, [viewerTimeZone])

    useEffect(() => {
        const fetchData = async () => {
            if (show && data) {
                setIsLoading(true);
                try {
                    const headers = {
                        Authorization: `Bearer ${token}`,
                    };

                    const getExpenseLogResponse = await axios.get(
                        `${baseURL}/api/Account${API_END_POINTS.userAccountDetailById}?id=${data}`,
                        {
                            headers,
                        }
                    );
                    setSingleUserData(getExpenseLogResponse.data);
                } catch (error) {
                    console.error('Error fetching data:', error);
                } finally {
                    setIsLoading(false);
                }
            } else if (!show) {
                setSingleUserData(null);
                setPresence(null);
            }
        };
        fetchData();
    }, [show, data, token, baseURL]);

    useEffect(() => {
        if (!show || !showPresence || !userId) return;

        const unsub = subscribeUserPresence((payload) => {
            if (Number(payload.orgUserId) !== userId) return
            setPresence({
                ...payload,
                lastLeaveUtc: payload.lastLeaveUtc || singleUserData?.result?.lastLeaveDt || null,
            })
        })

        fetchPresenceSnapshot(undefined)
            .unwrap()
            .then((res: any) => {
                const rows = res?.result || res?.Result || []
                if (!Array.isArray(rows)) return
                const row = rows.find((r: any) => Number(r?.orgUserId ?? r?.OrgUserId) === userId)
                if (!row) {
                    setPresence((prev) => prev || {
                        orgUserId: userId,
                        isOnline: false,
                        lastLeaveUtc: singleUserData?.result?.lastLeaveDt || null,
                    })
                    return
                }
                setPresence({
                    orgUserId: userId,
                    isOnline: !!(row?.isOnline ?? row?.IsOnline),
                    connectedOnUtc: row?.connectedOnUtc ?? row?.ConnectedOnUtc ?? null,
                    lastLeaveUtc: row?.lastLeaveUtc ?? row?.LastLeaveUtc ?? singleUserData?.result?.lastLeaveDt ?? null,
                    fullName: row?.fullName ?? row?.FullName ?? null,
                    email: row?.email ?? row?.Email ?? null,
                })
            })
            .catch(() => { /* presence optional */ })

        return unsub
    }, [show, showPresence, userId, fetchPresenceSnapshot, singleUserData?.result?.lastLeaveDt])

    const isOnline = showPresence
        ? !!(presence?.isOnline)
        : !!singleUserData?.result?.active
    const leaveDt = presence?.lastLeaveUtc || singleUserData?.result?.lastLeaveDt
    const loginDt = singleUserData?.result?.lastLoginDt
    const loginSourceId = singleUserData?.result?.lastLoginSourceId
    const presenceTooltipHtml = buildPresenceTooltipHtml({
        isOnline,
        dateTimeLabel: isOnline
            ? formatUtcInViewerTz(loginDt)
            : formatUtcInViewerTz(leaveDt),
        sourceId: loginSourceId,
    })

    const handleError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
        event.currentTarget.src = '/media/avatars/blank.png';
    };

    return (
        <>
            <Modal
                aria-hidden='true'
                dialogClassName='modal-dialog ss-modal-100 ss-user-detail-modal'
                contentClassName='ss-user-detail-content'
                fullscreen
                show={show}
                onHide={handleClose}
            >
                <div className='modal-header'>
                    <h2 className='mb-0'>{labelKey.userDetails}</h2>
                    <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={handleClose}>
                        <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
                    </div>
                </div>

                <div className='modal-body py-lg-10 px-lg-10'>
                    {isLoading && <h4 className='d-flex align-items-center justify-content-center text-dark'>{labelKey.loading}...</h4>}
                    <div className='card mb-5 mb-xl-10'>
                        <div className='card-body py-0'>
                            <div className='d-flex flex-wrap flex-sm-nowrap mb-3'>
                                <div className='me-7 mb-4'>
                                    <div className='symbol symbol-100px symbol-lg-160px symbol-fixed position-relative ss-user-list-avatar'>
                                        <img src={singleUserData?.result?.imageUrl ? singleUserData?.result?.imageUrl : '/media/avatars/blank.png'} alt='' onError={handleError} />
                                        <span
                                            className={`ss-user-online-dot ss-user-online-dot--lg ${isOnline ? 'is-online' : 'is-offline'}`}
                                            data-tooltip-id="user-detail-presence"
                                            data-tooltip-html={presenceTooltipHtml}
                                            aria-label={isOnline ? 'User online' : 'User offline'}
                                        />
                                    </div>
                                </div>

                                <div className='flex-grow-1'>
                                    <div className='d-flex gap-20 align-items-start flex-wrap mb-2'>
                                        <div className='d-flex flex-column'>
                                            <div className='d-flex align-items-center mb-2'>
                                                <p className='text-gray-800 text-hover-primary fs-2 fw-bold me-1 mb-0 d-flex align-items-center gap-2 text-capitalize'>
                                                    <span>{singleUserData && singleUserData?.result?.firstName} </span>
                                                    <span>{singleUserData && singleUserData?.result?.middleName}</span>
                                                    <span>{singleUserData && singleUserData?.result?.lastName}</span>
                                                </p>
                                                <span>
                                                    <KTSVG
                                                        path='/media/icons/duotune/general/gen026.svg'
                                                        className='svg-icon-1 svg-icon-primary'
                                                    />
                                                </span>
                                                {showPresence && (
                                                    <span className={`badge fs-8 fw-semibold ms-2 ${isOnline ? 'badge-light-success' : 'badge-light-secondary'}`}>
                                                        {isOnline ? 'Online' : 'Offline'}
                                                    </span>
                                                )}
                                            </div>

                                            <div className='fw-semibold fs-6 mb-4 pe-2'>
                                                <p
                                                    className='d-flex align-items-center text-gray-400 me-5 mb-2'
                                                >
                                                    <b className='text-dark'>{labelKey.role}: &nbsp;</b>
                                                    {singleUserData && singleUserData?.result?.userRole?.roleName}
                                                </p>
                                                <p className='d-flex align-items-center text-gray-400 mb-2'
                                                >
                                                    <b className='text-dark'>{labelKey.email}: &nbsp;</b>
                                                    <KTSVG
                                                        path='/media/icons/duotune/communication/com011.svg'
                                                        className='svg-icon-4 me-1'
                                                    />
                                                    {singleUserData && singleUserData?.result?.email}
                                                </p>
                                                <p className='d-flex align-items-center text-gray-400 mb-2'
                                                >
                                                    <b className='text-dark'>{labelKey.cellPhone}: &nbsp;</b>
                                                    {singleUserData && singleUserData?.result?.cellPhone}
                                                </p>
                                                <p className='d-flex align-items-center text-gray-400 mb-2'
                                                >
                                                    <b className='text-dark'>{labelKey.businessPhone}: &nbsp;</b>
                                                    {singleUserData && singleUserData?.result?.businessPhone}
                                                </p>
                                            </div>
                                        </div>

                                        <div className='d-flex flex-column'>
                                            <div className='fw-semibold fs-6 mb-4 pe-2 mt-10'>
                                                <p
                                                    className='d-flex align-items-center text-gray-400 me-5 mb-2'
                                                >
                                                    <b className='text-dark'>{labelKey.userType}: &nbsp;</b>
                                                    {singleUserData && singleUserData?.result?.userType?.value}
                                                </p>
                                                <p
                                                    className='d-flex align-items-center text-gray-400 me-5 mb-2'
                                                >
                                                    <b className='text-dark'>{labelKey.ssUserId}: &nbsp;</b>
                                                    {singleUserData && singleUserData?.result?.userAccountID}
                                                </p>
                                                <p
                                                    className='d-flex align-items-center text-gray-400 me-5 mb-2'
                                                >
                                                    <b className='text-dark'>{labelKey.employeeId}: &nbsp;</b>
                                                    {singleUserData && singleUserData?.result?.employeeId}
                                                </p>
                                                <p className='d-flex align-items-center text-gray-400 mb-2'
                                                >
                                                    <b className='text-dark'>{labelKey.bcEmployeeId}: &nbsp;</b>
                                                    {singleUserData && singleUserData?.result?.bcEmployeeId}
                                                </p>
                                                {singleUserData?.result?.userRole?.roleID === constraintConfig.roleID.role4 &&
                                                    <p className='d-flex align-items-center text-gray-400 mb-2'
                                                    >
                                                        <b className='text-dark'>{labelKey.approval$Limit}: &nbsp;</b>
                                                        {singleUserData && singleUserData?.result?.mgrApproveMaxLimit}
                                                    </p>}
                                            </div>
                                        </div>

                                        <div className='d-flex flex-column'>


                                            <div className='fw-semibold fs-6 mb-4 pe-2 mt-10'>
                                                <p
                                                    className='d-flex align-items-center text-gray-400 me-5 mb-2'
                                                >
                                                    <b className='text-dark'>{labelKey.organization}: &nbsp;</b>
                                                    {singleUserData && singleUserData?.result?.userOrg?.userInOrgName}
                                                </p>
                                                <p className='d-flex align-items-center text-gray-400 mb-2'
                                                >
                                                    <b className='text-dark'>{labelKey.manager}: &nbsp;</b>
                                                    {singleUserData && singleUserData?.result?.managerName}
                                                </p>
                                                <p className='d-flex align-items-center text-gray-400 mb-2'
                                                >
                                                    <b className='text-dark'>{labelKey.department}: &nbsp;</b>
                                                    {singleUserData && singleUserData?.result?.department?.title}
                                                </p>
                                                <p className='d-flex align-items-center text-gray-400 mb-2'
                                                >
                                                    <b className='text-dark'>{labelKey.designation}: &nbsp;</b>
                                                    {singleUserData && singleUserData?.result?.designation?.title}
                                                </p>
                                            </div>
                                        </div>

                                        <div className='d-flex flex-column'>
                                            <div className='fw-semibold fs-6 mb-4 pe-2 mt-10'>
                                                <p className='d-flex align-items-center text-gray-400 mb-2'
                                                >
                                                    <b className='text-dark w-200px'>{labelKey.excludeSupportUser}: &nbsp;</b>
                                                    <span className="fs-7 text-dark">{singleUserData && singleUserData?.result?.excludeSupportUser === true ?
                                                        <span className="badge badge-light-success fs-7 fw-semibold">{labelKey.yes}</span> :
                                                        <span className="badge badge-light-danger fs-7 fw-semibold">{labelKey.no}</span>}</span>
                                                </p>
                                                {singleUserData?.result?.userRole?.roleID === constraintConfig.roleID.role4 && constraintConfig.roleID.role10 ?
                                                    <p className='d-flex align-items-center text-gray-400 mb-2'
                                                    >
                                                        <b className='text-dark w-200px'>{labelKey.orgReportsPermision}: &nbsp;</b>
                                                        <span className="fs-7 text-dark">{singleUserData && singleUserData?.result?.reportPermision === true ?
                                                            <span className="badge badge-light-success fs-7 fw-semibold">{labelKey.allowed}</span> :
                                                            <span className="badge badge-light-danger fs-7 fw-semibold">{labelKey.notAllowed}</span>}</span>
                                                    </p> : null}
                                                {singleUserData?.result?.userRole?.roleID === constraintConfig.roleID.role4 && constraintConfig.roleID.role5 ?
                                                    <p className='d-flex align-items-center text-gray-400 mb-2'
                                                    >
                                                        <b className='text-dark w-200px'>{labelKey.excludeMileageCalcLogic}: &nbsp;</b>
                                                        <span className="fs-7 text-dark">{singleUserData && singleUserData?.result?.excludeMileageCalculationLogic === true ?
                                                            <span className="badge badge-light-success fs-7 fw-semibold">{labelKey.allowed}</span> :
                                                            <span className="badge badge-light-danger fs-7 fw-semibold">{labelKey.notAllowed}</span>}</span>
                                                    </p> : null}

                                                {singleUserData?.result?.userRole?.roleID === constraintConfig.roleID.role4 && constraintConfig.roleID.role10 ?
                                                    <>
                                                        {singleUserData?.result?.userPermission?.map((permissions: any, index: any) => (
                                                            <p
                                                                key={index}
                                                                className='d-flex align-items-center text-gray-400 me-5 mb-2'
                                                            >
                                                                <b className='text-dark w-200px'>{permissions.key}: &nbsp;</b>
                                                                {permissions.value ? (
                                                                    <span className="badge badge-light-success fs-7 fw-semibold">{labelKey.allowed}</span>
                                                                ) : (
                                                                    <span className="badge badge-light-danger fs-7 fw-semibold">{labelKey.notAllowed}</span>
                                                                )}
                                                            </p>
                                                        ))}
                                                    </> : null}
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-5 h-100">
                                        <div className="border border-gray-300 border-dashed rounded w-100 min-w-125px py-3 px-4 mx-3 mb-3 h-100">
                                            <div className="d-flex mt-3">
                                                <p className="fs-6 fw-bold text-gray-700 mb-0 text-start w-175px">{labelKey.delegateManager}:</p>
                                                <span className="fw-semibold">{singleUserData && singleUserData?.result?.delegateManager?.value}</span>
                                            </div>
                                            <div className="d-flex mt-3">
                                                <p className="fs-6 fw-bold text-gray-700 mb-0 text-start w-175px">{labelKey.delegateStartDate}:</p>
                                                <span className="fw-semibold"><DateComp formattedDate={singleUserData && singleUserData?.result?.delegateStartDate} /></span>
                                            </div>
                                            <div className="d-flex mt-3">
                                                <p className="fs-6 fw-bold text-gray-700 mb-0 text-start w-175px">{labelKey.delegateEndDate}:</p>
                                                <span className="fw-semibold"><DateComp formattedDate={singleUserData && singleUserData?.result?.delegateEndDate} /></span>
                                            </div>
                                            <div className="d-flex mt-3">
                                                <p className="fs-6 fw-bold text-gray-700 mb-0 text-start w-175px">{labelKey.importFromCCFeedDate}:</p>
                                                <span className="fw-semibold"><DateComp formattedDate={singleUserData && singleUserData?.result?.ccFeedFrom} /></span>
                                            </div>
                                            <div className="d-flex mt-3">
                                                <p className="fs-6 fw-bold text-gray-700 mb-0 text-start w-175px">{labelKey.allowedExpenseType}:</p>
                                                <span className="fw-semibold">{singleUserData && singleUserData?.result?.expenseType?.value}</span>
                                            </div>
                                            <div className="d-flex mt-3">
                                                <p className="fs-6 fw-bold text-gray-700 mb-0 text-start w-175px">{labelKey.allowedMileageType}:</p>
                                                <span className="fw-semibold">{singleUserData && singleUserData?.result?.locationType?.value}</span>
                                            </div>

                                            <div className="d-flex mt-3">
                                                <p className="fs-6 fw-bold text-gray-700 mb-0 text-start w-175px">{labelKey.officeLocation}:</p>
                                                <span className="fw-semibold w-50">{singleUserData?.result?.location?.value ? singleUserData?.result?.location?.value : "N/A"}</span>
                                            </div>
                                            <div className="d-flex mt-3">
                                                <p className="fs-6 fw-bold text-gray-700 mb-0 text-start w-175px">{labelKey.locationType}:</p>
                                                <span className="fw-semibold">{singleUserData?.result?.locationType?.value ? singleUserData?.result?.locationType?.value : "N/A"}</span>
                                            </div>

                                            <div className="d-flex mt-3">
                                                <p className="fs-6 fw-bold text-gray-700 mb-0 text-start w-175px">{labelKey.division}:</p>
                                                <span className="fw-semibold">{singleUserData?.result?.division?.title ? singleUserData?.result?.division?.title : "N/A"}</span>
                                            </div>
                                            <div className="d-flex mt-3">
                                                <p className="fs-6 fw-bold text-gray-700 mb-0 text-start w-175px">{labelKey.businessExt}:</p>
                                                <span className="fw-semibold"> {singleUserData?.result?.businessExt ? singleUserData?.result?.businessExt : "N/A"}</span>
                                            </div>
                                            <div className="d-flex mt-3">
                                                <p className="fs-6 fw-bold text-gray-700 mb-0 text-start w-175px">{labelKey.timeZone}:</p>
                                                <span className="fw-semibold"> {singleUserData?.result?.userTimeZone?.title ? singleUserData?.result?.userTimeZone?.title : "N/A"}</span>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-5 h-100">
                                        <div className="border border-gray-300 border-dashed rounded w-100 min-w-125px py-3 px-4 mx-3 mb-3 h-100">
                                            <div className="d-flex mt-3">
                                                <p className="fs-6 fw-bold text-gray-700 mb-0 text-start w-200px">{labelKey.findAddress}:</p>
                                                <span className="fw-semibold w-50"> {singleUserData && singleUserData?.result?.findAddress}</span>
                                            </div>
                                            <div className="d-flex mt-3">
                                                <p className="fs-6 fw-bold text-gray-700 mb-0 text-start w-200px">{labelKey.addressLine1}:</p>
                                                <span className="fw-semibold"> {singleUserData && singleUserData?.result?.addressLine1}</span>
                                            </div>
                                            <div className="d-flex mt-3">
                                                <p className="fs-6 fw-bold text-gray-700 mb-0 text-start w-200px">{labelKey.addressLine2}:</p>
                                                <span className="fw-semibold"> {singleUserData?.result?.addressLine2 ? singleUserData?.result?.addressLine2 : "N/A"}</span>
                                            </div>
                                            <div className="d-flex mt-3">
                                                <p className="fs-6 fw-bold text-gray-700 mb-0 text-start w-200px">{labelKey.longitude}:</p>
                                                <span className="fw-semibold"> {singleUserData?.result?.longitude ? singleUserData?.result?.longitude : "N/A"}</span>
                                            </div>
                                            <div className="d-flex mt-3">
                                                <p className="fs-6 fw-bold text-gray-700 mb-0 text-start w-200px">{labelKey.latitude}:</p>
                                                <span className="fw-semibold"> {singleUserData?.result?.latitude ? singleUserData?.result?.latitude : "N/A"}</span>
                                            </div>
                                            <div className="d-flex mt-3">
                                                <p className="fs-6 fw-bold text-gray-700 mb-0 text-start w-200px">{labelKey.officeDistanceSingleTrip}:</p>
                                                <span className="fw-semibold">
                                                    {singleUserData?.result?.homeToOfficeDistanceSingleTrip
                                                        ? parseFloat(singleUserData?.result?.homeToOfficeDistanceSingleTrip).toFixed(2)
                                                        : "N/A"}
                                                </span>
                                            </div>
                                            <div className="d-flex mt-3">
                                                <p className="fs-6 fw-bold text-gray-700 mb-0 text-start w-200px">{labelKey.officeDistanceRoundTrip}:</p>
                                                <span className="fw-semibold">
                                                    {singleUserData?.result?.homeToOfficeDistanceRoundTrip
                                                        ? parseFloat(singleUserData?.result?.homeToOfficeDistanceRoundTrip).toFixed(2)
                                                        : "N/A"}
                                                </span>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='card-footer border-0 pt-0 pb-5 px-9'>
                            <div className='d-flex flex-wrap gap-6 align-items-center ss-user-presence-footer'>
                                <div className='d-inline-flex align-items-center gap-2'>
                                    <span className='fw-semibold text-muted'>{labelKey.lastLogin}:</span>
                                    <LoginSourceIcon sourceId={loginSourceId} tooltipId="user-detail-login-source" />
                                    <span className='text-gray-800 fs-7'><DateTimeComp formattedDate={loginDt} /></span>
                                </div>
                                <div className='d-inline-flex align-items-center gap-2'>
                                    <span className='fw-semibold text-muted'>{labelKey.lastLeave}:</span>
                                    <LoginSourceIcon sourceId={loginSourceId} tooltipId="user-detail-login-source" />
                                    <span className='text-gray-800 fs-7'><DateTimeComp formattedDate={leaveDt} /></span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Tooltip id="user-detail-presence" place="top" />
                <Tooltip id="user-detail-login-source" place="top" />
            </Modal>
        </>
    )
}

export default UserDetailModal
